import { useGraphQLQuery } from '@betterment/graphql';

import { ProductCategoryOptionsOrder } from '../../graphql/schema';
import {
  HomeProductOnboardingOptionsDocument,
  HomeProductOnboardingOptionsQuery,
} from '../graphql/homeProductOnboardingOptions.generated';
import { HomeProductBundlesQuery } from '../graphql/productBundles.generated';

export type CheckingOptionInManualReview = HomeProductOnboardingOptionsQuery['productOnboardingOptions'][number] & {
  __typename: 'CheckingAccountCreationOption';
};

type UseProductOnboardingOptionsParams = {
  order?: ProductCategoryOptionsOrder;
  bundles?: HomeProductBundlesQuery['productBundles'];
};

export const useProductOnboardingOptions = (params?: UseProductOnboardingOptionsParams) => {
  const { order, bundles } = params || {};

  const { data } = useGraphQLQuery(HomeProductOnboardingOptionsDocument, {
    throwOnError: true,
    variables: { ...(order ? { orderingOption: order } : {}) },
  });

  if (!data) return { data };

  const { productOnboardingOptions: options, envelopes } = data;

  const bundledProductIds = new Set(
    (bundles || [])
      .filter(bundle => bundle.isInProgress)
      .flatMap(bundle => bundle.productOnboardingOptions)
      .map(p => p.id)
  );

  const optionsNotInBundles = options.filter(option => !bundledProductIds.has(option.id));

  const hasEnvelopes = !!envelopes.length;
  const crossSellOptions = hasEnvelopes ? optionsNotInBundles : [];
  const initialOptions = !hasEnvelopes
    ? optionsNotInBundles.filter(o => !('hasScheduledMeeting' in o && o.hasScheduledMeeting))
    : [];
  const goalCreationIntents = data.goalCreationIntents.filter(i => i.isUserSelected);
  const checkingReceivedInvitations = data.checkingReceivedInvitations.filter(i =>
    ['INVITE_SENT', 'ACCOUNT_CREATED'].includes(i.status)
  );
  const checkingOptionInManualReview = optionsNotInBundles
    .filter(o => o.__typename === 'CheckingAccountCreationOption')
    .find(o => 'isInManualReview' in o && o.isInManualReview);
  const inProgressOptions = goalCreationIntents.length > 0 ? [] : initialOptions.filter(o => o.isInProgress);
  const notInProgressOptions = initialOptions.filter(o => !o.isInProgress);

  return {
    data: {
      crossSellOptions,
      inProgressOptions,
      notInProgressOptions,
      intents: {
        goalCreationIntents,
        checkingReceivedInvitations,
        checkingOptionInManualReview,
      },
    },
  };
};
